/**
 * FilterBar configuration builder for the Inspections listing.
 *
 * Produces a FilterConfig[] array that represents every current filter,
 * respecting:
 *  - context-hidden rules (building/buildingStatus hidden in building/series scope;
 *    account hidden in account scope)
 *  - global-territory gate (territory hidden when hasGlobalTerritoriesApplied())
 *  - optional integration gates (subcontractor, tce, tceErrors, irol, irolErrors,
 *    livsafe, livsafeErrors, inspectionType)
 *  - active/inactive toggle state via FilterToggle
 *  - Unassigned sentinel for technician and territory
 *  - Status icons via Option.icon (React.ReactNode)
 *  - Building subtitle (address) via Option.subtitle
 *  - PagedMultiSelect loaders for building, account, tags, and territory
 *  - TreeMultiSelect for asset
 *
 * The FilterBar accepts FilterConfig[].active to decide which filters to render
 * in the active row vs. in the FilterToggle popup. Locked filters cannot be toggled.
 *
 * Returns:
 *  - filters: FilterConfig[] — pass directly to <FilterBar filters={filters} />
 *  - toggleItems: FilterToggleItem[] — available when the caller needs to render
 *    a custom toggle (FilterBar builds this internally from filters, so this is
 *    provided as a convenience for external toolbars).
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React from 'react';
import { hasGlobalTerritoriesApplied } from '@helpers/territories';
import { unassignedLookupOption, withoutUnassignedDuplicates, withUnassignedFirstPage, withUnassignedSelected } from '@helpers/unassignedLookup';
import { INSPECTIONS_DATE_RANGE_PRESETS, CUSTOM_RANGE_TYPE, toFilterSelectedPresetId, fromFilterPresetId, computeRangeForPresetId } from '../adapters/inspectionsDateRangePresets';
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/** Letter-avatar fallback when listing facade omits technician color (matches develop MultiSelect). */
const DEFAULT_TECHNICIAN_AVATAR_COLOR = '#000000';
/** Formats a Date as M/D/YYYY — matches the backend DATE_FORMAT used by Kendo::DateFilterable. */
function formatLocalDate(date) {
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}
/**
 * Converts a date string to a Date object.
 * Accepts both legacy ISO format (YYYY-MM-DD) and US format (M/D/YYYY or MM/DD/YYYY).
 */
function toDate(value) {
    if (!value)
        return null;
    // US format: M/D/YYYY or MM/DD/YYYY (the canonical stored format after fix)
    const usMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
    if (usMatch) {
        const month = Number(usMatch[1]);
        const day = Number(usMatch[2]);
        const year = Number(usMatch[3]);
        const date = new Date(year, month - 1, day);
        return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
    }
    // ISO format: YYYY-MM-DD (backwards compat for persisted preferences)
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!isoMatch)
        return null;
    const year = Number(isoMatch[1]);
    const month = Number(isoMatch[2]);
    const day = Number(isoMatch[3]);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}
/** Convert DateRange.value → InspectionDateRange for onChange (calendar edits = custom_range). */
function fromDateRange(value) {
    const hasDate = value.start != null || value.end != null;
    return {
        start: value.start ? formatLocalDate(value.start) : null,
        end: value.end ? formatLocalDate(value.end) : null,
        rangeType: hasDate ? CUSTOM_RANGE_TYPE : null
    };
}
function isActive(activeFilterKeys, field) {
    return activeFilterKeys.includes(field);
}
/** Build a React element icon from FontAwesome class + color. */
function statusIcon(iconClass, color) {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    return React.createElement('i', { className: iconClass, style: { color }, 'aria-hidden': 'true' });
}
// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useInspectionsFilterConfig(opts) {
    var _a;
    const { scope, filters, activeFilterKeys, onChange, onToggle, frequenciesForSelect, statusesForSelect, technicianTeamsForSelect, techniciansForSelect, inactiveTechniciansForSelect = [], assetsForSelect, subcontractorsForSelect, inspectionTypesForSelect, includeInactive = false, onIncludeInactiveChange, includeInactiveLabel, loadBuildings, loadAccounts, loadTerritories, loadTags, loadAdditionalTerritories, loadSelectedBuildings, loadSelectedAccounts, loadSelectedTerritories, loadSelectedTags, loadSelectedAdditionalTerritories, optionalFilters = {} } = opts;
    const isBuildingScope = scope.type === 'building' || scope.type === 'inspectionSeries';
    const isAccountScope = scope.type === 'account';
    const globalTerritoriesApplied = hasGlobalTerritoriesApplied();
    // Helper to update a single filter field
    const set = (field, value) => {
        onChange(Object.assign(Object.assign({}, filters), { [field]: value }));
    };
    // ── Build filter configs ──────────────────────────────────────────────────
    const filterList = [];
    // --- scheduledDate (date range, active by default) ---
    filterList.push({
        type: 'dateRange',
        field: 'scheduledDate',
        label: 'Scheduled Date',
        active: isActive(activeFilterKeys, 'scheduledDate'),
        value: { start: toDate(filters.scheduledDate.start), end: toDate(filters.scheduledDate.end) },
        presets: INSPECTIONS_DATE_RANGE_PRESETS,
        selectedPresetId: toFilterSelectedPresetId(filters.scheduledDate.rangeType),
        onPresetSelect: (id) => {
            const rangeType = fromFilterPresetId(id);
            if (rangeType === CUSTOM_RANGE_TYPE) {
                set('scheduledDate', {
                    start: filters.scheduledDate.start,
                    end: filters.scheduledDate.end,
                    rangeType: CUSTOM_RANGE_TYPE
                });
            }
            else {
                const range = computeRangeForPresetId(id);
                set('scheduledDate', Object.assign(Object.assign({}, range), { rangeType }));
            }
        },
        onChange: (v) => set('scheduledDate', fromDateRange(v))
    });
    // --- frequency (multi-select, active by default) ---
    filterList.push({
        type: 'multiSelect',
        field: 'frequency',
        label: 'Frequency',
        active: isActive(activeFilterKeys, 'frequency'),
        value: filters.frequency,
        options: frequenciesForSelect.map((o) => ({ value: o.value, label: o.label })),
        onChange: (v) => set('frequency', v)
    });
    // --- building (paged multi-select, hidden in building/series scope) ---
    if (!isBuildingScope) {
        filterList.push({
            type: 'pagedMultiSelect',
            field: 'building',
            label: 'Building',
            active: isActive(activeFilterKeys, 'building'),
            value: filters.building,
            loadOptions: loadBuildings,
            loadSelectedValues: loadSelectedBuildings,
            onChange: (v) => set('building', v)
        });
    }
    // --- status (multi-select with icons, active by default) ---
    filterList.push({
        type: 'multiSelect',
        field: 'status',
        label: 'Status',
        active: isActive(activeFilterKeys, 'status'),
        value: filters.status,
        options: statusesForSelect.map((s) => ({
            value: s.value,
            label: s.label,
            icon: s.icon && s.color ? statusIcon(s.icon, s.color) : undefined
        })),
        onChange: (v) => set('status', v)
    });
    // --- technician (multi-select with avatar, active by default, Unassigned sentinel) ---
    // Listing facade supplies { label, value, color, photoUrl }; map photoUrl→avatarUrl for
    // Quimby Avatar. Fall back to '#000' only when the server omits color entirely.
    // Inactive options merge when includeInactive is on. Selected inactive IDs stay visible
    // after uncheck so the chip does not go blank (IP-15930 frozen decision #6).
    // MassUpdateModal must continue consuming techniciansForSelect (active-only) — never this merged list.
    const mapTechOption = (t) => {
        var _a, _b;
        return ({
            value: t.value,
            label: t.label,
            avatarUrl: (_a = t.photoUrl) !== null && _a !== void 0 ? _a : t.avatarUrl,
            color: (_b = t.color) !== null && _b !== void 0 ? _b : DEFAULT_TECHNICIAN_AVATAR_COLOR
        });
    };
    const selectedTechnicianIds = new Set(((_a = filters.technician) !== null && _a !== void 0 ? _a : []).map(Number));
    const inactiveOptionsForFilter = includeInactive
        ? inactiveTechniciansForSelect
        : inactiveTechniciansForSelect.filter((t) => selectedTechnicianIds.has(Number(t.value)));
    filterList.push({
        type: 'multiSelectWithAvatar',
        field: 'technician',
        label: 'Technician',
        active: isActive(activeFilterKeys, 'technician'),
        value: filters.technician,
        options: [
            unassignedLookupOption(),
            ...withoutUnassignedDuplicates([...techniciansForSelect, ...inactiveOptionsForFilter]).map(mapTechOption)
        ],
        includeInactive,
        onIncludeInactiveChange,
        includeInactiveLabel,
        onChange: (v) => set('technician', v)
    });
    // --- asset (tree multi-select, active by default) ---
    filterList.push({
        type: 'treeMultiSelect',
        field: 'asset',
        label: 'Assets',
        active: isActive(activeFilterKeys, 'asset'),
        value: filters.asset,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        options: assetsForSelect,
        onChange: (v) => set('asset', v)
    });
    // --- technicianTeam (multi-select, inactive by default) ---
    filterList.push({
        type: 'multiSelect',
        field: 'technicianTeam',
        label: 'Technician Team',
        active: isActive(activeFilterKeys, 'technicianTeam'),
        value: filters.technicianTeam,
        options: technicianTeamsForSelect.map((o) => ({ value: o.value, label: o.label })),
        onChange: (v) => set('technicianTeam', v)
    });
    // --- buildingStatus (boolean, hidden in building/series scope, inactive by default) ---
    if (!isBuildingScope) {
        filterList.push({
            type: 'boolean',
            field: 'buildingStatus',
            label: 'Building Active',
            active: isActive(activeFilterKeys, 'buildingStatus'),
            value: filters.buildingStatus,
            onChange: (v) => set('buildingStatus', v)
        });
    }
    // --- account (paged multi-select, hidden in account scope, inactive by default) ---
    if (!isAccountScope) {
        filterList.push({
            type: 'pagedMultiSelect',
            field: 'account',
            label: 'Account',
            active: isActive(activeFilterKeys, 'account'),
            value: filters.account,
            loadOptions: loadAccounts,
            loadSelectedValues: loadSelectedAccounts,
            onChange: (v) => set('account', v)
        });
    }
    // --- noAccess (boolean, inactive by default) ---
    filterList.push({
        type: 'boolean',
        field: 'noAccess',
        label: 'No Access',
        active: isActive(activeFilterKeys, 'noAccess'),
        value: filters.noAccess,
        onChange: (v) => set('noAccess', v)
    });
    // --- zipCode (text, inactive by default) ---
    filterList.push({
        type: 'text',
        field: 'zipCode',
        label: 'Zip Code',
        active: isActive(activeFilterKeys, 'zipCode'),
        value: filters.zipCode,
        onChange: (v) => set('zipCode', v)
    });
    // --- referenceNumber (text, inactive by default) ---
    filterList.push({
        type: 'text',
        field: 'referenceNumber',
        label: 'Reference Number',
        active: isActive(activeFilterKeys, 'referenceNumber'),
        value: filters.referenceNumber,
        onChange: (v) => set('referenceNumber', v)
    });
    // --- workOrder (text, inactive by default) ---
    filterList.push({
        type: 'text',
        field: 'workOrder',
        label: 'Work Order',
        active: isActive(activeFilterKeys, 'workOrder'),
        value: filters.workOrder,
        onChange: (v) => set('workOrder', v)
    });
    // --- tags (paged multi-select, inactive by default) ---
    filterList.push({
        type: 'pagedMultiSelect',
        field: 'tags',
        label: 'Inspection Tags',
        active: isActive(activeFilterKeys, 'tags'),
        value: filters.tags,
        loadOptions: loadTags,
        loadSelectedValues: loadSelectedTags,
        onChange: (v) => set('tags', v)
    });
    // --- buildingTags (paged multi-select, inactive by default) ---
    filterList.push({
        type: 'pagedMultiSelect',
        field: 'buildingTags',
        label: 'Building Tags',
        active: isActive(activeFilterKeys, 'buildingTags'),
        value: filters.buildingTags,
        loadOptions: loadTags,
        loadSelectedValues: loadSelectedTags,
        onChange: (v) => set('buildingTags', v)
    });
    // --- accountTags (paged multi-select, inactive by default) ---
    filterList.push({
        type: 'pagedMultiSelect',
        field: 'accountTags',
        label: 'Account Tags',
        active: isActive(activeFilterKeys, 'accountTags'),
        value: filters.accountTags,
        loadOptions: loadTags,
        loadSelectedValues: loadSelectedTags,
        onChange: (v) => set('accountTags', v)
    });
    // --- workOrderTags (paged multi-select, inactive by default) ---
    filterList.push({
        type: 'pagedMultiSelect',
        field: 'workOrderTags',
        label: 'Work Order Tags',
        active: isActive(activeFilterKeys, 'workOrderTags'),
        value: filters.workOrderTags,
        loadOptions: loadTags,
        loadSelectedValues: loadSelectedTags,
        onChange: (v) => set('workOrderTags', v)
    });
    // --- inspectionDate (date range, inactive by default) ---
    filterList.push({
        type: 'dateRange',
        field: 'inspectionDate',
        label: 'Inspection Date',
        active: isActive(activeFilterKeys, 'inspectionDate'),
        value: { start: toDate(filters.inspectionDate.start), end: toDate(filters.inspectionDate.end) },
        presets: INSPECTIONS_DATE_RANGE_PRESETS,
        selectedPresetId: toFilterSelectedPresetId(filters.inspectionDate.rangeType),
        onPresetSelect: (id) => {
            const rangeType = fromFilterPresetId(id);
            if (rangeType === CUSTOM_RANGE_TYPE) {
                set('inspectionDate', {
                    start: filters.inspectionDate.start,
                    end: filters.inspectionDate.end,
                    rangeType: CUSTOM_RANGE_TYPE
                });
            }
            else {
                const range = computeRangeForPresetId(id);
                set('inspectionDate', Object.assign(Object.assign({}, range), { rangeType }));
            }
        },
        onChange: (v) => set('inspectionDate', fromDateRange(v))
    });
    // --- dueDate (date range, inactive by default) ---
    filterList.push({
        type: 'dateRange',
        field: 'dueDate',
        label: 'Due Date',
        active: isActive(activeFilterKeys, 'dueDate'),
        value: { start: toDate(filters.dueDate.start), end: toDate(filters.dueDate.end) },
        presets: INSPECTIONS_DATE_RANGE_PRESETS,
        selectedPresetId: toFilterSelectedPresetId(filters.dueDate.rangeType),
        onPresetSelect: (id) => {
            const rangeType = fromFilterPresetId(id);
            if (rangeType === CUSTOM_RANGE_TYPE) {
                set('dueDate', {
                    start: filters.dueDate.start,
                    end: filters.dueDate.end,
                    rangeType: CUSTOM_RANGE_TYPE
                });
            }
            else {
                const range = computeRangeForPresetId(id);
                set('dueDate', Object.assign(Object.assign({}, range), { rangeType }));
            }
        },
        onChange: (v) => set('dueDate', fromDateRange(v))
    });
    // --- visitDate (date range, inactive by default) ---
    // Matches inspections having ANY visit scheduled in the range. Scheduled Date only ever
    // reflects the primary visit, so on a long-running job it can sit months in the past
    // while later visits go unnoticed; this is the filter that surfaces them.
    filterList.push({
        type: 'dateRange',
        field: 'visitDate',
        label: 'Visit Date',
        active: isActive(activeFilterKeys, 'visitDate'),
        value: { start: toDate(filters.visitDate.start), end: toDate(filters.visitDate.end) },
        presets: INSPECTIONS_DATE_RANGE_PRESETS,
        selectedPresetId: toFilterSelectedPresetId(filters.visitDate.rangeType),
        onPresetSelect: (id) => {
            const rangeType = fromFilterPresetId(id);
            if (rangeType === CUSTOM_RANGE_TYPE) {
                set('visitDate', {
                    start: filters.visitDate.start,
                    end: filters.visitDate.end,
                    rangeType: CUSTOM_RANGE_TYPE
                });
            }
            else {
                const range = computeRangeForPresetId(id);
                set('visitDate', Object.assign(Object.assign({}, range), { rangeType }));
            }
        },
        onChange: (v) => set('visitDate', fromDateRange(v))
    });
    // --- inspectionId (text, inactive by default) ---
    filterList.push({
        type: 'text',
        field: 'inspectionId',
        label: 'Inspection #',
        active: isActive(activeFilterKeys, 'inspectionId'),
        value: filters.inspectionId,
        onChange: (v) => set('inspectionId', v)
    });
    // --- territory (paged multi-select, Unassigned sentinel, hidden when global territories) ---
    if (!globalTerritoriesApplied) {
        filterList.push({
            type: 'pagedMultiSelect',
            field: 'territory',
            label: 'Territory',
            active: isActive(activeFilterKeys, 'territory'),
            value: filters.territory,
            loadOptions: (query, cursor) => __awaiter(this, void 0, void 0, function* () {
                return withUnassignedFirstPage(yield loadTerritories(query, cursor), query, cursor);
            }),
            loadSelectedValues: withUnassignedSelected(loadSelectedTerritories),
            onChange: (v) => set('territory', v)
        });
    }
    // --- additionalTerritory (paged multi-select, inactive by default) ---
    filterList.push({
        type: 'pagedMultiSelect',
        field: 'additionalTerritory',
        label: 'Territory (refine)',
        active: isActive(activeFilterKeys, 'additionalTerritory'),
        value: filters.additionalTerritory,
        loadOptions: loadAdditionalTerritories,
        loadSelectedValues: loadSelectedAdditionalTerritories,
        onChange: (v) => set('additionalTerritory', v)
    });
    // ── Optional / integration filters ────────────────────────────────────────
    if (optionalFilters.subcontractor) {
        filterList.push({
            type: 'multiSelect',
            field: 'subcontractor',
            label: 'Subcontractor',
            active: isActive(activeFilterKeys, 'subcontractor'),
            value: filters.subcontractor,
            options: (subcontractorsForSelect !== null && subcontractorsForSelect !== void 0 ? subcontractorsForSelect : []).map((o) => ({ value: o.value, label: o.label })),
            onChange: (v) => set('subcontractor', v)
        });
    }
    if (optionalFilters.tce) {
        filterList.push({
            type: 'checkbox',
            field: 'tce',
            label: 'TCE Inspections',
            active: isActive(activeFilterKeys, 'tce'),
            value: filters.tce,
            onChange: (v) => set('tce', v)
        });
    }
    if (optionalFilters.tceErrors) {
        filterList.push({
            type: 'checkbox',
            field: 'tceErrors',
            label: 'Failed TCE Submissions',
            active: isActive(activeFilterKeys, 'tceErrors'),
            value: filters.tceErrors,
            onChange: (v) => set('tceErrors', v)
        });
    }
    if (optionalFilters.irol) {
        filterList.push({
            type: 'checkbox',
            field: 'irol',
            label: 'IROL Inspections',
            active: isActive(activeFilterKeys, 'irol'),
            value: filters.irol,
            onChange: (v) => set('irol', v)
        });
    }
    if (optionalFilters.irolErrors) {
        filterList.push({
            type: 'checkbox',
            field: 'irolErrors',
            label: 'Failed IROL Submissions',
            active: isActive(activeFilterKeys, 'irolErrors'),
            value: filters.irolErrors,
            onChange: (v) => set('irolErrors', v)
        });
    }
    if (optionalFilters.livsafe) {
        filterList.push({
            type: 'checkbox',
            field: 'livsafe',
            label: 'LivSafe Inspections',
            active: isActive(activeFilterKeys, 'livsafe'),
            value: filters.livsafe,
            onChange: (v) => set('livsafe', v)
        });
    }
    if (optionalFilters.livsafeErrors) {
        filterList.push({
            type: 'checkbox',
            field: 'livsafeErrors',
            label: 'Failed LivSafe Submissions',
            active: isActive(activeFilterKeys, 'livsafeErrors'),
            value: filters.livsafeErrors,
            onChange: (v) => set('livsafeErrors', v)
        });
    }
    if (optionalFilters.inspectionType) {
        filterList.push({
            type: 'multiSelect',
            field: 'inspectionType',
            label: 'Inspection Type',
            active: isActive(activeFilterKeys, 'inspectionType'),
            value: filters.inspectionType,
            options: (inspectionTypesForSelect !== null && inspectionTypesForSelect !== void 0 ? inspectionTypesForSelect : []).map((o) => ({ value: o.value, label: o.label })),
            onChange: (v) => set('inspectionType', v)
        });
    }
    // ── Build toggle items ────────────────────────────────────────────────────
    const toggleItems = filterList.map((f) => {
        var _a;
        return ({
            field: f.field,
            label: f.label,
            active: (_a = f.active) !== null && _a !== void 0 ? _a : false,
            onToggle: () => onToggle(f.field)
        });
    });
    return { filters: filterList, toggleItems };
}
